import BlockUi from '@availity/block-ui';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { getShapeData } from '../../../../actions/gtfsStaticActions';
import { getRtVehiclePositionData, getTripStopsData } from '../../../../actions/vehiclesHistoryActions';
import Api from '../../../../api/api';
import { RtVehiclePosition, TripStopData } from '../../../../types/busHistoryTypes';
import { InfoboxProps, PolylineProps, PushpinProps } from '../../../../types/MapModels';
import { DropDownStateType } from '../../../../types/types';
import TransitMap from '../../../shared/AzureMap/TransitMap';
import { createStopInfoInfobox, createStopPushpin } from '../../../shared/StopMapElements';
import { createVehicleMapElements } from '../../../shared/VehicleMapElements';
import StopsScheduleTable from './StopsScheduleTable';
import { VehicleHistorySlider } from './VehicleHistorySlider';

const initialDropDownState: DropDownStateType = {
    options: [],
    selectedValue: '',
};

type Styles = {
    busy: React.CSSProperties,
    block1: React.CSSProperties,
    box1: React.CSSProperties,
    box2: React.CSSProperties,
    sliderSegment: React.CSSProperties,
};

const styles: Styles = {
    busy: {
        height: 'auto',
    },
    block1: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 14,
    },
    box1: {
        flexGrow: 1,
        minWidth: '50%',
        height: 500,
    },
    box2: {
        minWidth: '25%',
        maxHeight: 500,
        justifyContent: 'space-around',
    },
    sliderSegment: {
        paddingLeft: 24,
        paddingRight: 24,
    },
};

interface VehicleHistoryPaneProps {
    agencyId: string,
    serviceDate: string,
    vehicleId: string,
    tripId: string,
    shapeId: string,
}

export const VehicleHistoryPane: React.FC<VehicleHistoryPaneProps> = (props) => {
    const [formBlockingState, setFormBlockingState] = useState(false);
    const [center, setCenter] = useState<CoordinatePair>();
    const [mapBoundsState, setMapBoundsState] = useState<any>();
    const [polylineState, setPolylineState] = useState<PolylineProps>();
    const [tripsState] = useState(initialDropDownState);
    const [vehiclePositionsState, setVehiclePositionsState] = useState<RtVehiclePosition[]>([]);
    const [currentPositionState, setCurrentPositionState] = useState<RtVehiclePosition>();
    const [sliderValueState, setSliderValueState] = useState<number>(-1);
    const [tripStopsState, setTripStopsState] = useState<TripStopData[]>([]);
    const [selectedStopState, setSelectedStopState] = useState<TripStopData>();

    useEffect(() => {
        clearStates();
        (async (agencyId: string) => {
            const { Latitude, Longitude } = await Api.getOrgLocation(agencyId);
            setCenter([Latitude, Longitude]);
            if (props.vehicleId) {
                await retrieveTripsData(props.vehicleId, props.serviceDate);
            }
        })(props.agencyId);
    }, [props.agencyId, props.serviceDate, props.vehicleId, props.tripId]);

    useEffect(() => {
        const position = vehiclePositionsState[sliderValueState];
        setCurrentPositionState(position);
        return () => setCurrentPositionState(undefined);
    }, [sliderValueState, vehiclePositionsState]);


    useEffect(() => {
        if (!tripsState.selectedValue) {
            setSliderValueState(-1);
            setVehiclePositionsState([]);
            setCurrentPositionState(undefined);
            setTripStopsState([]);
            setPolylineState(undefined);
        }
    }, [tripsState]);

    const handleSliderValueChange = (value: number) => {
        setSliderValueState(value);
    };

    const retrieveTripsData = async (_vehicleId: string, _selectedDate: string) => {
        if (props.agencyId && props.shapeId) {
            setFormBlockingState(true);
            try {
                const [vehiclePositions, tripStops, shapePoints] = await Promise.all([
                    getRtVehiclePositionData(props.agencyId, props.tripId, props.vehicleId),
                    getTripStopsData(props.agencyId, props.tripId),
                    getShapeData(props.agencyId, props.serviceDate, props.shapeId),
                ]);
                setVehiclePositionsState(vehiclePositions);
                setTripStopsState(tripStops);
                setPolylineState({
                    locations: shapePoints.map(({ latitude, longitude }) => [latitude, longitude]),
                    options: {
                        strokeColor: 'orangered',
                        strokeThickness: 3,
                    },
                });
                const lats = shapePoints.map(p => p.latitude);
                const lons = shapePoints.map(p => p.longitude);
                setMapBoundsState({
                    north: Math.max(...lats),
                    south: Math.min(...lats),
                    east: Math.max(...lons),
                    west: Math.min(...lons),
                });
                setSliderValueState(0);
            } catch {
                setVehiclePositionsState([]);
                setTripStopsState([]);
                setPolylineState(undefined);
            }
            finally {
                setFormBlockingState(false);
            }
        } else {
            setVehiclePositionsState([]);
            setTripStopsState([]);
            setPolylineState(undefined);
        }
    };

    const clearStates = () => {
        setVehiclePositionsState([]);
        setCurrentPositionState(undefined);
        setTripStopsState([]);
        setSliderValueState(-1);
        setPolylineState(undefined);
    };

    const stopPushpins = React.useMemo<PushpinProps[]>(() => {
        if (!selectedStopState) {
            return [];
        }

        return [createStopPushpin({
            location: [selectedStopState.latitude, selectedStopState.longitude],
            selected: true,
        })];
    }, [selectedStopState]);

    const stopInfoboxes = React.useMemo<InfoboxProps[]>(() => {
        if (!selectedStopState) {
            return [];
        }

        return [createStopInfoInfobox({
            location: [selectedStopState.latitude, selectedStopState.longitude],
            label: `${selectedStopState.stopId}, ${moment.parseZone(selectedStopState.scheduledTime).format('h:mm A')}`,
            visible: true,
        })];
    }, [selectedStopState]);

    const vehiclePushpins = React.useMemo<PushpinProps[]>(() => {
        if (!currentPositionState) {
            return [];
        }

        return [createVehicleMapElements({
            location: [currentPositionState.latitude, currentPositionState.longitude],
            vehicleId: props.vehicleId,
            description: `at ${moment.parseZone(currentPositionState.vehicleTime).format('h:mm:ss A')}`,
        }).pushpin];
    }, [currentPositionState, props.vehicleId]);

    const vehicleInfoboxes = React.useMemo<InfoboxProps[]>(() => {
        if (!currentPositionState) {
            return [];
        }

        return [createVehicleMapElements({
            location: [currentPositionState.latitude, currentPositionState.longitude],
            vehicleId: props.vehicleId,
            description: `at ${moment.parseZone(currentPositionState.vehicleTime).format('h:mm:ss A')}`,
        }).infobox];
    }, [currentPositionState, props.vehicleId]);

    return (
        <div>
            <BlockUi blocking={formBlockingState} style={styles.busy}>
                <div style={styles.sliderSegment}>
                    {polylineState && (!vehiclePositionsState || vehiclePositionsState.length < 1) &&
                        <p>- no history data found -</p>
                    }
                    {vehiclePositionsState && vehiclePositionsState.length > 0 &&
                        <VehicleHistorySlider
                            maxValue={vehiclePositionsState.length - 1}
                            sliderValue={sliderValueState}
                            handleSliderChange={handleSliderValueChange}
                            currentTime={currentPositionState?.vehicleTime}
                            currentDelay={currentPositionState?.delaySec}
                        />
                    }
                </div>
                <div style={styles.block1}>
                    <div style={styles.box1}>
                        {polylineState &&
                            <TransitMap
                                map={{
                                    center,
                                    options: {
                                        bounds: mapBoundsState,
                                    },
                                }}
                                polylines={polylineState && [polylineState]}
                                pushpins={[...stopPushpins, ...vehiclePushpins]}
                                infoboxes={[...stopInfoboxes, ...vehicleInfoboxes]}
                            />
                        }
                    </div>
                    <div style={styles.box2}>
                        {tripStopsState.length > 0 &&
                            <StopsScheduleTable
                                rows={tripStopsState}
                                selectedRow={selectedStopState}
                                setSelectedRow={setSelectedStopState}
                            />
                        }
                    </div>
                </div>
            </BlockUi>
        </div>
    );
};
